import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { Brain, TrendingUp, Shield } from 'lucide-react';
import { trackPredictionRead } from '@/lib/analytics';

const API_BASE = '';

export default function PredictionCard({ slug }) {
  const [pred, setPred] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentLang } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/matches/${slug}/ai-prediction?lang=${currentLang}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setPred(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, currentLang]);

  // Track prediction view
  useEffect(() => { if (pred && slug && !pred.fallback) trackPredictionRead(slug); }, [pred, slug]);

  if (loading || !pred || pred.fallback || pred.confidence_score === 0) return null;

  const hw = pred.home_win_probability || 0;
  const dp = pred.draw_probability || 0;
  const aw = pred.away_win_probability || 0;
  const conf = pred.confidence_score || 0;
  const confLabel = pred.confidence_label || 'Low';
  const reasoning = pred.reasoning_summary || '';

  const confColors = {
    Low: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    Medium: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    High: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  };

  return (
    <div className="border border-violet-500/20 bg-violet-500/5 rounded-xl p-5 mb-4" data-testid="prediction-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-violet-400" />
          <h3 className="text-sm font-semibold text-violet-400 uppercase tracking-wider"
            style={{ fontFamily: 'var(--font-heading)' }}>
            {t('prediction.aiPrediction', 'AI Prediction')}
          </h3>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${confColors[confLabel] || confColors.Low}`}
          data-testid="confidence-badge">
          <Shield className="w-3 h-3 inline mr-1" />
          {confLabel} ({conf}%)
        </span>
      </div>

      {/* Probability Bars */}
      <div className="space-y-3 mb-4">
        <ProbBar label={pred.home_team || 'Home'} pct={hw} color="bg-emerald-500" testId="prob-home" />
        <ProbBar label={t('prediction.draw', 'Draw')} pct={dp} color="bg-slate-400" testId="prob-draw" />
        <ProbBar label={pred.away_team || 'Away'} pct={aw} color="bg-rose-500" testId="prob-away" />
      </div>

      {/* Reasoning */}
      {reasoning && reasoning.length > 20 && (
        <div className="bg-slate-900/50 rounded-lg p-3 mt-3" data-testid="prediction-reasoning">
          <div className="flex items-center gap-1.5 mb-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              {t('prediction.analysis', 'Analysis')}
            </span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">{reasoning}</p>
        </div>
      )}
    </div>
  );
}

function ProbBar({ label, pct, color, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-400 font-medium truncate max-w-[180px]">{label}</span>
        <span className="text-xs font-bold text-slate-200 tabular-nums">{pct}%</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
